import type { Article } from "./types";

export const orgtoolsVsCascade: Article = {
  slug: "orgtools-vs-cascade",
  kind: "comparison",
  title: "Orgtools vs Cascade: Decision Record vs Strategy Execution",
  description:
    "Cascade maps objectives to plans and tracks strategy execution. Orgtools records the decision events behind the strategy and watches for drift. How they compare and when to use each.",
  lede: "Cascade is a strategy execution platform built around strategy maps that link objectives to plans. Orgtools is not a strategy map. It finds the decisions your plan depends on, shows what your people and agents decide as the work moves, and flags when the work and the plan disagree.",
  datePublished: "2026-06-26",
  sections: [
    {
      type: "prose",
      heading: "What Sets Orgtools Apart from Cascade",
      body: "Cascade is built to visualize and execute a strategy: strategy maps, cascading goals, dashboards, and scenario planning that show how objectives connect across the organization. It answers how the strategy is structured and how it is progressing.\n\nCascade comes closer than most tools to linking strategy to execution. What it does not capture are the decisions made as the work moves: which call changed an objective, what was traded off, who made it, and whether it held. When people and agents do a week's work in a day, those calls outrun any map. Orgtools finds the decisions a plan depends on, shows who decided what wherever they decided it, and watches for drift, drag, and decay, so a strategy that quietly stops being followed flags itself.",
    },
    {
      type: "table",
      heading: "Head-to-Head Comparison",
      columns: ["Capability", "Orgtools", "Cascade"],
      rows: [
        ["Structured decision record", "Yes", "Strategy map of objectives"],
        ["Captures reasoning behind an objective", "Yes", "Objective definitions and plans"],
        ["Named accountability for the call", "Yes", "Objective owners"],
        ["Outcome tracked against the decision", "Yes", "Progress against objectives"],
        ["Drift, drag, and decay signals", "Yes", "Goal metrics and reporting"],
        ["Strategy mapping and cascading", "Links via anchors", "Core strength"],
      ],
    },
    {
      type: "prose",
      heading: "When to Choose Cascade",
      body: "Choose Cascade when the core need is structuring and visualizing a strategy: mapping objectives to plans, cascading them across teams, and reporting progress, with scenario planning and executive summaries. For a leadership team that wants a single picture of how the strategy connects and where it stands, that is what Cascade is built for.",
    },
    {
      type: "prose",
      heading: "When to Choose Orgtools",
      body: "Choose Orgtools when the work moves faster than the map: objectives that change with no recorded call, decisions made in sessions and threads that never reach the plan, and a strategy that drifts because nothing flags when the work pulls away from it. A strategy map shows the structure. Orgtools shows the calls being made against it, and lets you step in on the ones that matter. Used together, the map stays honest.",
    },
    {
      type: "cta",
      heading: "See the Calls Behind Your Strategy",
      href: "/platform",
      label: "Explore the Platform",
    },
  ],
  faq: [
    {
      question: "Is Orgtools a strategy execution platform like Cascade?",
      answer:
        "Not in the same sense. Cascade structures and tracks a strategy through maps and cascading goals. Orgtools finds the decisions a strategy depends on and shows what gets decided as the work moves. They address different layers and can be used together.",
    },
    {
      question: "Does Orgtools have strategy maps?",
      answer:
        "Orgtools links decisions to strategy through strategic anchors rather than a visual strategy map. The emphasis is on the decision record and its outcome, not on visualizing the objective hierarchy.",
    },
    {
      question: "How is a decision record different from a strategy map?",
      answer:
        "A strategy map shows how objectives connect. A decision record captures how each was decided: the reasoning, the data, the owner, and whether the outcome matched. The map is the structure. The record is the accountability behind it.",
    },
  ],
};
